import { ApiError } from '~/types/api'

/**
 * 短信/邮箱验证码倒计时。
 * 到期时间写入 localStorage，弹窗关闭 / 刷新页面后仍生效，结束前不可再次发送。
 * key 支持传入 getter（如 () => `login-email-code:${account}`），
 * 使倒计时按具体标识符（邮箱/手机号）隔离，避免切换标识符后被之前的倒计时误伤。
 *
 * 冷却时长以服务端为准：发码被频控（429）时响应头 Retry-After 给出真实剩余秒数，
 * 倒计时改用它；头缺席时才用本地默认 seconds（服务端 ResendCooldown 可配置，不能写死）。
 */
export function useSmsCountdown(key: string | (() => string), seconds = 60) {
  const isDynamicKey = typeof key === 'function'
  const getKey = () => (isDynamicKey ? (key as () => string)() : (key as string))
  // 在 composable 顶层获取：send() 属事件回调，内部再调 useI18n / useNotify 会脱离 setup 上下文
  const { t } = useI18n()
  const { error: notifyError } = useNotify()

  const remaining = ref(0)
  const pending = ref(false)

  let timer: ReturnType<typeof setInterval> | null = null
  /** 本轮倒计时的总秒数（Retry-After 可能大于默认 seconds），用作显示上限 */
  let capSeconds = seconds

  const storageKey = (k: string) => `sms-cd:${k}`
  const sentKey = (k: string) => `sms-sent:${k}`

  /** 发码生命周期：同一标识在滑动窗口内最多发码 SEND_LIMIT 次，超限不再发码（防轰炸） */
  const SEND_LIMIT = 5
  const SEND_WINDOW_MS = 10 * 60 * 1000

  const readSendTimes = (k: string): number[] => {
    if (!import.meta.client) return []
    try {
      const arr = JSON.parse(localStorage.getItem(sentKey(k)) ?? '[]')
      return Array.isArray(arr) ? arr.filter((v: unknown) => Number.isFinite(v)) : []
    } catch {
      return []
    }
  }

  const writeSendTimes = (k: string, times: number[]) => {
    if (!import.meta.client) return
    if (times.length) localStorage.setItem(sentKey(k), JSON.stringify(times))
    else localStorage.removeItem(sentKey(k))
  }

  /** 记一次发送；超限返回 false（不发送、不进入倒计时） */
  const tryRecordSend = (): boolean => {
    if (!import.meta.client) return true
    const k = getKey()
    const now = Date.now()
    const times = readSendTimes(k).filter(t => now - t < SEND_WINDOW_MS)
    if (times.length >= SEND_LIMIT) {
      writeSendTimes(k, times)
      notifyError(t('login.codeSendLimited'))
      return false
    }
    times.push(now)
    writeSendTimes(k, times)
    return true
  }

  const readEndsAt = (k: string): number => {
    if (!import.meta.client) return 0
    const raw = localStorage.getItem(storageKey(k))
    const val = raw ? Number(raw) : 0
    return Number.isFinite(val) ? val : 0
  }

  const writeEndsAt = (k: string, val: number) => {
    if (!import.meta.client) return
    if (val > 0) localStorage.setItem(storageKey(k), String(val))
    else localStorage.removeItem(storageKey(k))
  }

  const calcLeft = (endsAt: number) => {
    const ms = endsAt - Date.now()
    if (ms <= 0) return 0
    // ceil 对齐整秒；上限 capSeconds 防止刚启动时出现「默认秒数 + 1」
    return Math.min(capSeconds, Math.ceil(ms / 1000))
  }

  const clearTimer = () => {
    if (timer == null) return
    clearInterval(timer)
    timer = null
  }

  const sync = () => {
    const k = getKey()
    const endsAt = readEndsAt(k)
    const left = calcLeft(endsAt)
    remaining.value = left
    if (left > 0) return true
    clearTimer()
    if (endsAt) writeEndsAt(k, 0)
    return false
  }

  const ensureTimer = () => {
    if (!import.meta.client || timer != null) return
    // 高频对齐墙钟，避免 1s tick + ceil 造成停顿/跳秒
    timer = setInterval(sync, 250)
  }

  const begin = (secs: number) => {
    capSeconds = Math.max(seconds, secs)
    writeEndsAt(getKey(), Date.now() + secs * 1000)
    remaining.value = secs
    clearTimer()
    ensureTimer()
  }

  /** 依据当前 key 重新计算剩余时间；用于初始化、key 变化、页面重新可见时 */
  const refreshFromKey = () => {
    if (!import.meta.client) return
    const k = getKey()
    const endsAt = readEndsAt(k)
    // 刷新 / 切换标识后恢复的倒计时可能来自更长的 Retry-After：按剩余秒数放宽上限
    capSeconds = Math.max(seconds, Math.ceil((endsAt - Date.now()) / 1000))
    const left = calcLeft(endsAt)
    remaining.value = left
    if (left > 0) {
      ensureTimer()
    } else {
      clearTimer()
      if (endsAt) writeEndsAt(k, 0)
    }
  }

  if (import.meta.client) {
    refreshFromKey()

    if (isDynamicKey) {
      watch(getKey, () => refreshFromKey())
    }

    const onVisible = () => {
      if (document.visibilityState !== 'visible') return
      refreshFromKey()
    }
    document.addEventListener('visibilitychange', onVisible)
    onScopeDispose(() => {
      document.removeEventListener('visibilitychange', onVisible)
      clearTimer()
    })
  }

  const isCounting = computed(() => pending.value || remaining.value > 0)

  const send = async (callback?: () => Promise<unknown>) => {
    if (isCounting.value) return
    // 发码生命周期：窗口内次数超限则拦截（倒计时复位也发不出去）
    if (!tryRecordSend()) return

    // 先进入倒计时再请求，接口失败也不清零，避免连点刷验证码
    begin(seconds)
    pending.value = true
    try {
      await callback?.()
    } catch (error) {
      // 业务错误已由 api 拦截器 toast；频控响应带 Retry-After 时按服务端剩余秒数重新计时
      const retryAfter = error instanceof ApiError ? error.retryAfterSeconds : null
      if (retryAfter !== null && retryAfter > 0) begin(retryAfter)
    } finally {
      pending.value = false
    }
  }

  const reset = (countdown?: number) => {
    pending.value = false
    if (countdown) {
      begin(countdown)
      return
    }
    writeEndsAt(getKey(), 0)
    remaining.value = 0
    clearTimer()
  }

  const stop = () => {
    pending.value = false
    writeEndsAt(getKey(), 0)
    remaining.value = 0
    clearTimer()
  }

  return {
    remaining,
    isCounting,
    send,
    reset,
    stop,
  }
}
